import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Drawer,
  List,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout } from '../../store/slices/authSlice';
import authService from '../../api/services/authService';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useFeatureFlags } from '../../features';
import { useIsMobile } from '../../hooks/useIsMobile';

const Header: React.FC = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const adminNonMemberView = useAppSelector((state) => state.ui.adminNonMemberView);
  const FEATURES = useFeatureFlags();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setAdminMenuAnchor(null);
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const handleLogout = async () => {
    await authService.logout();   // H6: clears the httpOnly cookie server-side
    dispatch(logout());
    handleMenuClose();
    navigate('/');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleDashboard = () => {
    handleMenuClose();
    navigate('/dashboard');
  };

  const go = (path: string) => () => {
    handleMenuClose();
    navigate(path);
  };

  const isStaffish = user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superuser';
  const isAdminish = user?.role === 'admin' || user?.role === 'superuser';
  // Non-members keep role 'member' but their membership_status is non_member_*.
  // Visual language: green = members/staff, orange = non-members.
  const isNonMember = (user?.membership_status || '').toLowerCase().startsWith('non_member');

  // ---------- Shared nav items (rendered in both Menu and Drawer) ----------

  const baseItems = (
    <>
      <MenuItem onClick={handleDashboard}>Dashboard</MenuItem>
      <MenuItem onClick={handleProfile}>My Profile</MenuItem>

      {FEATURES.tennis && (
        <MenuItem onClick={go('/courts')}>Court Reservations</MenuItem>
      )}
      {FEATURES.kidsClub && user?.role === 'member' && (
        <MenuItem onClick={go('/kids-club')}>Kids Club Booking</MenuItem>
      )}

      {FEATURES.swim && user?.role === 'member' && (
        <MenuItem onClick={go('/swim')}>Swim Classes</MenuItem>
      )}
      {FEATURES.swim && user?.role === 'member' && (
        <MenuItem onClick={go('/swim/my')}>My Swim Enrollments</MenuItem>
      )}
      {FEATURES.swim && user?.role === 'member' && (
        <MenuItem onClick={go('/swim/schedule')}>My Swim Schedule</MenuItem>
      )}

      {isStaffish && (
        <MenuItem onClick={go('/admin/check-in')}>Front Desk Check-In</MenuItem>
      )}
      {isStaffish && (
        <MenuItem onClick={go('/admin/registration-qr')}>Registration QR</MenuItem>
      )}
      {isStaffish && !isAdminish && (
        <MenuItem onClick={go('/admin')}>Member Directory</MenuItem>
      )}
      {FEATURES.kidsClub && isStaffish && (
        <MenuItem onClick={go('/kids-club/staff')}>Kids Club Staff Dashboard</MenuItem>
      )}
      {FEATURES.swim && isStaffish && (
        <MenuItem onClick={go('/coach/schedule')}>Swim Coach Schedule</MenuItem>
      )}
    </>
  );

  // ---------- Admin nav items (the section that's flat on mobile, nested on desktop) ----------

  const adminItems = (
    <>
      {FEATURES.swim && (
        <>
          <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>SWIM</ListSubheader>
          <MenuItem onClick={go('/admin/swim-management')}>Pros &amp; Programs</MenuItem>
          <MenuItem onClick={go('/admin/swim/programs')}>Swim Programs</MenuItem>
          <MenuItem onClick={go('/admin/swim/class-levels')}>Skill Levels</MenuItem>
          <MenuItem onClick={go('/admin/swim/leagues')}>Leagues</MenuItem>
          <MenuItem onClick={go('/admin/swim/enrollments')}>Enrollments</MenuItem>
        </>
      )}

      {FEATURES.tennis && (
        <>
          <Divider />
          <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>TENNIS</ListSubheader>
          <MenuItem onClick={go('/admin/tennis-management')}>Pros &amp; Programs</MenuItem>
          <MenuItem onClick={go('/admin/tennis/enrollments')}>Enrollments</MenuItem>
        </>
      )}

      {FEATURES.kidsClub && (
        <>
          <Divider />
          <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>KIDS CLUB</ListSubheader>
          <MenuItem onClick={go('/kids-club/admin')}>Kids Club Admin</MenuItem>
        </>
      )}

      <Divider />
      <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>MEMBERS</ListSubheader>
      <MenuItem onClick={go('/non-member-management')}>Non-Member Management</MenuItem>

      <Divider />
      <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>SYSTEM</ListSubheader>
      <MenuItem onClick={go('/admin/feature-flags')}>Feature Flags</MenuItem>

      {(FEATURES.tennis || FEATURES.kidsClub) && (
        <>
          <Divider />
          <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.7rem', letterSpacing: '0.06em' }}>REPORTS</ListSubheader>
          <MenuItem onClick={go('/admin/programs/enrollments')}>All Program Enrollments</MenuItem>
        </>
      )}
    </>
  );

  const userManagementItem = isAdminish && (
    <MenuItem onClick={go('/admin')}>User Management</MenuItem>
  );

  const logoutItem = <MenuItem onClick={handleLogout}>Logout</MenuItem>;

  return (
    <AppBar position="sticky" color={isNonMember || adminNonMemberView ? 'warning' : 'primary'}>
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to={isAuthenticated ? '/dashboard' : '/'}
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 700,
          }}
        >
          Fig Swim
        </Typography>

        {isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Hide the welcome blurb on phones to free up horizontal room */}
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Welcome, {user?.first_name || 'Member'}
            </Typography>
            <IconButton onClick={handleMenuOpen} aria-label="account menu" sx={{ p: 0 }}>
              <Avatar
                src={user?.profile_photo_url || undefined}
                sx={{ width: 36, height: 36 }}
              >
                {!user?.profile_photo_url && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
              </Avatar>
            </IconButton>

            {isMobile ? (
              /* ----- Mobile: Drawer with a single flat list. No nested anchored submenu. ----- */
              <Drawer
                anchor="right"
                open={open}
                onClose={handleMenuClose}
                PaperProps={{ sx: { width: { xs: '85vw', sm: 320 }, maxWidth: 360 } }}
              >
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    {user?.first_name || 'Member'} • {user?.role}
                  </Typography>
                </Box>
                <Divider />
                <List dense>
                  {baseItems}
                  {isAdminish && (
                    <>
                      <Divider sx={{ mt: 1 }} />
                      <ListSubheader sx={{ lineHeight: 1.8, fontSize: '0.75rem', letterSpacing: '0.06em', fontWeight: 700 }}>
                        ADMIN
                      </ListSubheader>
                      {adminItems}
                      {userManagementItem}
                    </>
                  )}
                  <Divider sx={{ mt: 1 }} />
                  {logoutItem}
                </List>
              </Drawer>
            ) : (
              /* ----- Desktop: original Menu with nested anchored Admin submenu ----- */
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {baseItems}

                {isAdminish && (
                  <MenuItem onClick={handleAdminMenuOpen}>
                    <ListItemText>Admin</ListItemText>
                    <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                      <ChevronRightIcon fontSize="small" />
                    </ListItemIcon>
                  </MenuItem>
                )}
                {isAdminish && (
                  <Menu
                    anchorEl={adminMenuAnchor}
                    open={Boolean(adminMenuAnchor)}
                    onClose={handleAdminMenuClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {adminItems}
                  </Menu>
                )}

                {userManagementItem}
                {logoutItem}
              </Menu>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="inherit" component={Link} to="/login" sx={{ fontWeight: 600 }}>
              Login
            </Button>
            <Button variant="outlined" color="inherit" component={Link} to="/register" sx={{ fontWeight: 600 }}>
              Register
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
